import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  MultiplayerSession, 
  MultiplayerPlayer, 
  Message, 
  User,
  GameState,
  StoryOption,
  WinCondition,
  AdventurePlan,
  GameOutcome,
  EmojiReaction,
  AdventureVote,
  VotingState
} from '../types';
import { MessageList } from './MessageList';
import { PredictiveKeyboard } from './PredictiveKeyboard';
import { CountdownDisplay } from './CountdownDisplay';
import { EmojiReactionButton } from './EmojiReactionButton';
import { EmojiAnimation } from './EmojiAnimation';
import { Users, Wifi, WifiOff, Crown, Vote, Clock, Timer } from 'lucide-react';
import { 
  updateMultiplayerGameState, 
  selectMultiplayerPrediction,
  endMultiplayerGame,
  subscribeToMultiplayerEvents,
  getMultiplayerSession,
  sendEmojiReaction,
  startVoting,
  castVote,
  endVotingSession,
  passTurnToNextPlayer,
  publishStreamingChunk,
  publishStreamingComplete
} from '../services/multiplayer';
import { startStory, continueStory, getPredictions, getStoryOptions } from '../services/gemini';

interface MultiplayerGameProps {
  session: MultiplayerSession;
  user: User;
  onGameOver: (outcome: GameOutcome, reason: string) => void;
  onLeave: () => void;
  isDarkMode: boolean;
  authToken: string | null;
}

export const MultiplayerGame: React.FC<MultiplayerGameProps> = ({
  session: initialSession,
  user,
  onGameOver,
  onLeave,
  isDarkMode,
  authToken,
}) => {
  const [session, setSession] = useState<MultiplayerSession>(initialSession);
  const [gameState, setGameState] = useState<GameState>(GameState.LOADING_INITIAL);
  const [messages, setMessages] = useState<Message[]>(initialSession.messages || []);
  const [currentInput, setCurrentInput] = useState(initialSession.currentInput || '');
  const [suggestions, setSuggestions] = useState<string[]>(initialSession.suggestions || []);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [streamingPlayerId, setStreamingPlayerId] = useState<string | null>(null); // Track who is streaming
  const streamingPlayerIdRef = useRef<string | null>(null); // Ref to track current streaming player for immediate access
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(true);
  const [winCondition, setWinCondition] = useState<WinCondition | null>(initialSession.winCondition || null);
  const [adventurePlan, setAdventurePlan] = useState<AdventurePlan | null>(initialSession.adventurePlan as AdventurePlan | null);
  const [countdown, setCountdown] = useState<{ remaining: number; label: string } | null>(null);
  const [emojiReactions, setEmojiReactions] = useState<EmojiReaction[]>([]);
  const [shownReactionIds, setShownReactionIds] = useState<Set<string>>(new Set());
  
  // Voting state
  const [votingState, setVotingState] = useState<VotingState | null>(initialSession.votingState || null);
  const [votingTimeRemaining, setVotingTimeRemaining] = useState<number>(0);
  const [myVote, setMyVote] = useState<string | null>(null);
  const [votingResult, setVotingResult] = useState<{ selectedOption: StoryOption; wasTie: boolean } | null>(null);
  
  // Auto-send timer state
  const [autoSendTimeRemaining, setAutoSendTimeRemaining] = useState<number>(0);
  const [predictionsReady, setPredictionsReady] = useState<boolean>(initialSession.predictionsReady || false);
  
  // Track if it's this player's turn
  const isMyTurn = useMemo(() => session.currentTurnPlayerId === user.id, [session.currentTurnPlayerId, user.id]);
  const currentPlayer = useMemo(() => session.players.find(p => p.id === session.currentTurnPlayerId), [session.players, session.currentTurnPlayerId]);
  
  // Helper to update streaming player ID (both ref and state)
  const updateStreamingPlayerId = useCallback((playerId: string | null) => {
    streamingPlayerIdRef.current = playerId;
    setStreamingPlayerId(playerId);
  }, []);
  
  // Handle selecting a suggestion
  const handleSelectSuggestion = useCallback(async (word: string) => {
    if (!isMyTurn) return;
    
    if (session.turnMode === 'per_prediction') {
      // In per_prediction mode, use the API to select and switch turns
      try {
        const result = await selectMultiplayerPrediction(session.id, word);
        setSession(result.session);
        setCurrentInput(result.session.currentInput);
        
        // Load new suggestions
        setIsLoadingSuggestions(true);
        const newSuggestions = await getPredictions(messages, result.session.currentInput, 'cerebras/zai-glm-4.6', authToken);
        setSuggestions(newSuggestions);
        await updateMultiplayerGameState(session.id, {
          suggestions: newSuggestions,
        });
        setIsLoadingSuggestions(false);
      } catch (err) {
        console.error('Failed to select prediction:', err);
        setError(err instanceof Error ? err.message : 'Failed to select prediction');
      }
    } else {
      // In per_message mode, just append to input locally and sync
      const spacing = currentInput.length > 0 && !currentInput.endsWith(' ') ? ' ' : '';
      const newInput = currentInput + spacing + word;
      setCurrentInput(newInput);
      
      // Sync input to session
      await updateMultiplayerGameState(session.id, {
        currentInput: newInput,
      });
      
      // Load new suggestions
      setIsLoadingSuggestions(true);
      const newSuggestions = await getPredictions(messages, newInput, 'cerebras/zai-glm-4.6', authToken);
      setSuggestions(newSuggestions);
      await updateMultiplayerGameState(session.id, {
        suggestions: newSuggestions,
      });
      setIsLoadingSuggestions(false);
    }
  }, [isMyTurn, session, currentInput, messages, authToken]);
  
  // Handle backspace
  const handleBackspace = useCallback(async () => {
    if (!isMyTurn) return;
    
    const words = currentInput.trim().split(' ');
    if (words.length <= 1) {
      setCurrentInput('');
      await updateMultiplayerGameState(session.id, { currentInput: '' });
      return;
    }
    
    words.pop();
    const newInput = words.join(' ');
    setCurrentInput(newInput);
    
    // Sync to session
    await updateMultiplayerGameState(session.id, {
      currentInput: newInput,
    });
    
    // Load new suggestions
    setIsLoadingSuggestions(true);
    const newSuggestions = await getPredictions(messages, newInput, 'cerebras/zai-glm-4.6', authToken);
    setSuggestions(newSuggestions);
    await updateMultiplayerGameState(session.id, {
      suggestions: newSuggestions,
    });
    setIsLoadingSuggestions(false);
  }, [isMyTurn, currentInput, session.id, messages, authToken]);
  
  // Handle sending a message
  const handleSendMessage = useCallback(async () => {
    if (!currentInput.trim()) return;
    if (!isMyTurn && session.turnMode === 'per_message') return;
    
    const userMsg: Message = {
      id: Date.now().toString(),
      text: currentInput,
      sender: 'user',
      timestamp: Date.now(),
    };
    
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setCurrentInput('');
    setIsProcessingAI(true);
    setStreamingText('');
    updateStreamingPlayerId(user.id); // Mark that we're streaming
    
    // Sync user message
    await updateMultiplayerGameState(session.id, {
      newMessage: userMsg,
      currentInput: '',
    });
    
    try {
      // Get AI response with streaming
      const response = await continueStory(
        updatedMessages,
        currentInput,
        async (chunk) => {
          // Update local streaming text
          setStreamingText(prev => prev + chunk);
          // Publish chunk to all players
          try {
            await publishStreamingChunk(session.id, chunk);
          } catch (err) {
            console.error('Failed to publish streaming chunk:', err);
          }
        },
        'cerebras/zai-glm-4.6',
        winCondition || undefined,
        (thinking) => setIsThinking(thinking),
        session.difficulty,
        adventurePlan || undefined
      );
      
      setStreamingText('');
      setIsThinking(false);
      updateStreamingPlayerId(null);
      
      // Notify all players that streaming is complete
      try {
        await publishStreamingComplete(session.id);
      } catch (err) {
        console.error('Failed to publish streaming complete:', err);
      }
      
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: response.text,
        sender: 'ai',
        timestamp: Date.now(),
      };
      
      const finalMessages = [...updatedMessages, aiMsg];
      setMessages(finalMessages);
      
      // Sync AI message
      await updateMultiplayerGameState(session.id, {
        newMessage: aiMsg,
      });
      
      // Check for game outcome
      if (response.outcome) {
        await endMultiplayerGame(session.id, response.outcome, response.outcomeReason || '');
        onGameOver(response.outcome, response.outcomeReason || '');
        return;
      }
      
      // Handle countdown
      if (response.countdown) {
        if (response.countdown.action === 'start' && response.countdown.remaining !== undefined) {
          setCountdown({ remaining: response.countdown.remaining, label: response.countdown.label || 'Moves remaining' });
        } else if (response.countdown.action === 'decrement' && countdown) {
          const newRemaining = countdown.remaining - 1;
          if (newRemaining <= 0) {
            await endMultiplayerGame(session.id, 'lose', 'Time ran out!');
            onGameOver('lose', 'Time ran out!');
            return;
          }
          setCountdown({ ...countdown, remaining: newRemaining });
        } else if (response.countdown.action === 'cancel') {
          setCountdown(null);
        }
      }
      
      // Get next suggestions
      const newSuggestions = await getPredictions(finalMessages, '', 'cerebras/zai-glm-4.6', authToken);
      setSuggestions(newSuggestions);
      await updateMultiplayerGameState(session.id, {
        suggestions: newSuggestions,
      });
      
    } catch (err) {
      console.error('Error in game loop:', err);
      setError('AI response failed. Please try again.');
      updateStreamingPlayerId(null);
    } finally {
      setIsProcessingAI(false);
    }
  }, [currentInput, isMyTurn, session.turnMode, session.id, messages, winCondition, session.difficulty, adventurePlan, authToken, onGameOver, countdown, user.id]);

  // Subscribe to session events for real-time updates
  useEffect(() => {
    let cleanup: (() => void) | null = null;
    
    const subscribe = () => {
      cleanup = subscribeToMultiplayerEvents(
        session.id,
        (event) => {
          setIsConnected(true);
          
          // Handle player disconnected event
          if (event.type === 'player_disconnected') {
            const playerName = (event as any).playerName as string;
            const playerId = (event as any).playerId as string;
            if (playerId !== user.id) {
              setError(`${playerName || 'Other player'} has disconnected`);
              setTimeout(() => setError(null), 5000);
            }
          }
          
          // Handle player left event
          if (event.type === 'player_left') {
            const playerName = (event as any).playerName as string;
            const playerId = (event as any).playerId as string;
            if (playerId !== user.id) {
              setError(`${playerName || 'Other player'} has left the game`);
              setTimeout(() => setError(null), 5000);
            }
          }
          
          // Handle input updated event
          if (event.type === 'input_updated') {
            const currentInputValue = (event as any).currentInput as string;
            if (currentInputValue !== undefined) {
              setCurrentInput(currentInputValue);
            }
          }
          
          // Handle suggestions updated event
          if (event.type === 'suggestions_updated') {
            const suggestionsValue = (event as any).suggestions as string[];
            if (suggestionsValue) {
              setSuggestions(suggestionsValue);
            }
          }
          
          // Handle predictions ready event
          if (event.type === 'predictions_ready') {
            setPredictionsReady(true);
          }
          
          // Handle auto-send timer tick event
          if (event.type === 'auto_send_tick') {
            const autoSendStartTime = (event as any).autoSendStartTime as number;
            if (autoSendStartTime !== undefined) {
              setSession(prev => ({
                ...prev,
                autoSendStartTime,
              }));
            }
          }
          
          // Handle turn changed event
          if (event.type === 'turn_changed') {
            const currentTurnPlayerId = (event as any).currentTurnPlayerId as string;
            const autoSendStartTime = (event as any).autoSendStartTime as number;
            
            setSession(prev => ({
              ...prev,
              currentTurnPlayerId: currentTurnPlayerId || prev.currentTurnPlayerId,
              autoSendStartTime: autoSendStartTime !== undefined ? autoSendStartTime : prev.autoSendStartTime,
            }));
          }
          
          // Handle message sent event
          if (event.type === 'message_sent') {
            const message = (event as any).message as Message;
            if (message) {
              // Mark predictions as not ready when a message is sent
              setPredictionsReady(false);
              setMessages(prev => {
                // Check if message already exists (avoid duplicates)
                if (prev.some(m => m.id === message.id)) {
                  return prev;
                }
                return [...prev, message];
              });
            }
          }
          
          // Handle specific event types
          if (event.type === 'game_over') {
            const outcome = (event as any).outcome as GameOutcome;
            const reason = (event as any).reason as string || '';
            onGameOver(outcome, reason);
          }
          
          // Handle emoji reaction event
          if (event.type === 'emoji_reaction') {
            const reaction = (event as any).reaction as EmojiReaction;
            if (reaction) {
              setEmojiReactions(prev => [...prev, reaction]);
            }
          }
          
          // Handle AI streaming chunk event (from other players)
          if (event.type === 'ai_streaming_chunk') {
            const playerId = (event as any).playerId as string;
            const chunk = (event as any).chunk as string;
            
            // Only process streaming from other players
            if (playerId && playerId !== user.id && chunk !== undefined) {
              // Check if this is a new stream using ref for immediate access
              const isNewStream = streamingPlayerIdRef.current !== playerId;
              
              // Update streaming text: start fresh for new stream, append for continuation
              if (isNewStream) {
                setStreamingText(chunk);
              } else {
                setStreamingText(prev => prev + chunk);
              }
              
              // Update both ref and state (React batches these in event handlers)
              updateStreamingPlayerId(playerId);
            }
          }
          
          // Handle AI streaming complete event
          if (event.type === 'ai_streaming_complete') {
            const playerId = (event as any).playerId as string;
            
            // Clear streaming if it matches the player who was streaming
            if (streamingPlayerIdRef.current === playerId) {
              setStreamingText('');
              updateStreamingPlayerId(null);
            }
          }
          
          // Handle voting started event
          if (event.type === 'voting_started') {
            const options = (event as any).options as StoryOption[];
            const votingEndTime = (event as any).votingEndTime as number;
            if (options && votingEndTime) {
              setVotingState({
                options,
                votes: [],
                votingEndTime,
              });
              setMyVote(null);
              setVotingResult(null);
              setGameState(GameState.CHOOSING_STORY);
            }
          }
          
          // Handle vote cast event
          if (event.type === 'vote_cast') {
            const votes = (event as any).votes as AdventureVote[];
            if (votes && votingState) {
              setVotingState(prev => prev ? { ...prev, votes } : null);
            }
          }
          
          // Handle voting ended event
          if (event.type === 'voting_ended') {
            const selectedOption = (event as any).selectedOption as StoryOption;
            const wasTie = (event as any).wasTie as boolean;
            if (selectedOption) {
              setVotingResult({ selectedOption, wasTie: wasTie || false });
              // After a brief delay, transition to playing
              setTimeout(() => {
                setGameState(GameState.LOADING_INITIAL);
                setVotingResult(null);
                setVotingState(null);
              }, 3000);
            }
          }
        },
        (err) => {
          console.error('Connection error:', err);
          setIsConnected(false);
        }
      );
    };
    
    subscribe();
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [session.id, onGameOver, user.id]);
  
  // Clean up old shown reaction IDs periodically to prevent memory bloat
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setShownReactionIds(prev => {
        // Keep only IDs from the last 60 seconds
        const cutoffTime = Date.now() - 60000;
        const recentIds = new Set<string>();
        
        // Parse reaction IDs to check their timestamps
        Array.from(prev).forEach((id: string) => {
          const timestamp = parseInt(id.split('-')[0]);
          if (!isNaN(timestamp) && timestamp > cutoffTime) {
            recentIds.add(id);
          }
        });
        
        return recentIds;
      });
    }, 30000); // Run cleanup every 30 seconds
    
    return () => clearInterval(cleanupInterval);
  }, []);
  
  // Voting countdown timer effect
  useEffect(() => {
    if (!votingState || !votingState.votingEndTime) return;
    
    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((votingState.votingEndTime - Date.now()) / 1000));
      setVotingTimeRemaining(remaining);
      
      // If voting time expired and host, end voting
      if (remaining === 0 && session.hostId === user.id && !votingResult) {
        endVotingSession(session.id).catch(err => {
          console.error('Failed to end voting:', err);
          setError('Failed to end voting. Please try again.');
        });
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [votingState, session.id, session.hostId, user.id, votingResult]);
  
  // Auto-send timer effect
  useEffect(() => {
    if (!session.autoSendLimit || session.autoSendLimit === 0) return;
    if (!session.autoSendStartTime) return;
    if (!isMyTurn) {
      setAutoSendTimeRemaining(0);
      return;
    }
    // Don't start timer while AI is processing
    if (isProcessingAI) {
      setAutoSendTimeRemaining(0);
      return;
    }
    // Don't start timer while predictions are not ready
    if (!predictionsReady) {
      setAutoSendTimeRemaining(0);
      return;
    }
    
    const updateTimer = () => {
      const elapsed = (Date.now() - (session.autoSendStartTime || 0)) / 1000;
      const remaining = Math.max(0, session.autoSendLimit - elapsed);
      setAutoSendTimeRemaining(Math.ceil(remaining));
      
      // When time expires
      if (remaining <= 0 && isMyTurn) {
        if (currentInput.trim()) {
          // Auto-send if there's input
          try {
            handleSendMessage();
          } catch (err) {
            console.error('Auto-send failed:', err);
            setError('Auto-send failed. Please send manually.');
          }
        } else {
          // Pass turn if no input
          passTurnToNextPlayer(session.id).catch(err => {
            console.error('Failed to pass turn:', err);
            setError('Failed to pass turn. Please try again.');
          });
        }
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [session.autoSendLimit, session.autoSendStartTime, isMyTurn, currentInput, isProcessingAI, predictionsReady, handleSendMessage, session.id]);
  
  // Initialize game - start the story
  useEffect(() => {
    const initGame = async () => {
      // Only the host initializes the game
      if (session.hostId !== user.id) {
        // Non-host waits for updates
        if (messages.length > 0) {
          setGameState(GameState.PLAYING);
        }
        return;
      }
      
      // If messages already exist, we're resuming
      if (messages.length > 0) {
        setGameState(GameState.PLAYING);
        return;
      }
      
      try {
        setGameState(GameState.LOADING_INITIAL);
        
        // Get story options
        const options = await getStoryOptions(undefined, 'cerebras/zai-glm-4.6', session.difficulty, session.gameMode, session.genre);
        
        if (options.length > 0) {
          const selectedOption = options[0];
          
          // Store win condition and adventure plan
          if (selectedOption.winCondition) {
            setWinCondition(selectedOption.winCondition);
            await updateMultiplayerGameState(session.id, {
              winCondition: selectedOption.winCondition,
            });
          }
          if (selectedOption.adventurePlan) {
            setAdventurePlan(selectedOption.adventurePlan);
            await updateMultiplayerGameState(session.id, {
              adventurePlan: selectedOption.adventurePlan,
            });
          }
          
          // Start the story
          updateStreamingPlayerId(user.id); // Mark that we're streaming
          const introText = await startStory(
            async (chunk) => {
              setStreamingText(prev => prev + chunk);
              // Publish chunk to all players
              try {
                await publishStreamingChunk(session.id, chunk);
              } catch (err) {
                console.error('Failed to publish streaming chunk:', err);
              }
            },
            selectedOption.fullPrompt,
            'cerebras/zai-glm-4.6',
            (thinking) => setIsThinking(thinking),
            0,
            authToken
          );
          
          setStreamingText('');
          setIsThinking(false);
          updateStreamingPlayerId(null);
          
          // Notify all players that streaming is complete
          try {
            await publishStreamingComplete(session.id);
          } catch (err) {
            console.error('Failed to publish streaming complete:', err);
          }
          
          const introMsg: Message = {
            id: Date.now().toString(),
            text: introText,
            sender: 'ai',
            timestamp: Date.now(),
          };
          
          // Update local state
          setMessages([introMsg]);
          
          // Sync to session
          await updateMultiplayerGameState(session.id, {
            newMessage: introMsg,
          });
          
          // Get initial suggestions
          const newSuggestions = await getPredictions([introMsg], '', 'cerebras/zai-glm-4.6', authToken);
          setSuggestions(newSuggestions);
          await updateMultiplayerGameState(session.id, {
            suggestions: newSuggestions,
          });
          
          setGameState(GameState.PLAYING);
        }
      } catch (err) {
        console.error('Failed to initialize game:', err);
        setError('Failed to start the adventure. Please try again.');
        setGameState(GameState.GAME_OVER);
      }
    };
    
    initGame();
  }, [session.id, session.hostId, session.difficulty, session.gameMode, session.genre, user.id, authToken, messages.length]);
  


  
  // Handle emoji selection
  const handleEmojiSelect = useCallback(async (emoji: string) => {
    try {
      await sendEmojiReaction(session.id, emoji);
    } catch (err) {
      console.error('Failed to send emoji reaction:', err);
    }
  }, [session.id]);
  
  // Handle emoji animation complete
  const handleEmojiComplete = useCallback((reactionId: string) => {
    setEmojiReactions(prev => prev.filter(r => r.id !== reactionId));
    setShownReactionIds(prev => new Set(prev).add(reactionId));
  }, []);
  
  // Handle voting for an adventure
  const handleVote = useCallback(async (optionId: string) => {
    if (myVote) return; // Already voted
    
    try {
      await castVote(session.id, optionId);
      setMyVote(optionId);
    } catch (err) {
      console.error('Failed to cast vote:', err);
      setError('Failed to cast vote. Please try again.');
    }
  }, [session.id, myVote]);
  
  // Get vote count for an option
  const getVoteCount = useCallback((optionId: string) => {
    if (!votingState) return 0;
    return votingState.votes.filter(v => v.optionId === optionId).length;
  }, [votingState]);
  
  // Get voters for an option
  const getVoters = useCallback((optionId: string) => {
    if (!votingState) return [];
    return votingState.votes.filter(v => v.optionId === optionId).map(v => v.playerName);
  }, [votingState]);
  
  return (
    <div className={`flex flex-col h-full ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Multiplayer Header */}
      <div className={`px-4 py-2 flex items-center justify-between border-b ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {/* Connection status */}
        <div className={`flex items-center gap-2 text-sm ${
          isConnected 
            ? isDarkMode ? 'text-green-400' : 'text-green-600'
            : isDarkMode ? 'text-red-400' : 'text-red-600'
        }`}>
          {isConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
          <Users size={14} />
          <span>{session.players.length}</span>
        </div>
        
        {/* Turn indicator */}
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
          isMyTurn
            ? isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'
            : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
        }`}>
          {isMyTurn ? (
            <>
              <Crown size={14} />
              <span>Your Turn</span>
            </>
          ) : (
            <span>{currentPlayer?.username || currentPlayer?.name || 'Other player'}'s turn</span>
          )}
        </div>
        
        {/* Turn mode indicator */}
        <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {session.turnMode === 'per_message' ? 'Per Message' : 'Per Prediction'}
        </div>
      </div>
      
      {/* Win condition hint */}
      {winCondition && session.gameMode === 'adventure' && gameState === GameState.PLAYING && (
        <div className={`px-4 py-2 text-xs flex items-center gap-2 border-b ${
          isDarkMode 
            ? 'bg-gray-800 text-gray-400 border-gray-700' 
            : 'bg-gray-50 text-gray-500 border-gray-200'
        }`}>
          <span className="flex-1">🎯 Hint: {winCondition.hint}</span>
        </div>
      )}
      
      {/* Error banner */}
      {error && (
        <div className={`px-4 py-2 text-sm border-b ${
          isDarkMode ? 'bg-red-900 text-red-200 border-red-800' : 'bg-red-100 text-red-700 border-red-200'
        }`}>
          {error}
          <button onClick={() => setError(null)} className="ml-2 font-bold">✕</button>
        </div>
      )}
      
      {/* Other player's activity indicator (for per_prediction mode) */}
      {session.turnMode === 'per_prediction' && !isMyTurn && currentInput && (
        <div className={`px-4 py-2 text-xs flex items-center gap-2 border-b animate-pulse ${
          isDarkMode 
            ? 'bg-purple-900/30 text-purple-300 border-purple-800' 
            : 'bg-purple-50 text-purple-600 border-purple-200'
        }`}>
          <span>👀 {currentPlayer?.username || currentPlayer?.name} is typing: "{currentInput}"</span>
        </div>
      )}
      
      {/* Predictions loading indicator (when it's your turn but predictions not ready) */}
      {session.autoSendLimit > 0 && isMyTurn && !predictionsReady && gameState === GameState.PLAYING && (
        <div className={`px-4 py-2 text-xs flex items-center justify-between border-b ${
          isDarkMode 
            ? 'bg-blue-900/30 text-blue-300 border-blue-800' 
            : 'bg-blue-50 text-blue-600 border-blue-200'
        }`}>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin border-blue-500" />
            <span>Loading predictions...</span>
          </div>
          <div className="text-xs opacity-75">
            Timer will start when ready
          </div>
        </div>
      )}
      
      {/* Auto-send timer indicator */}
      {session.autoSendLimit > 0 && isMyTurn && predictionsReady && autoSendTimeRemaining > 0 && gameState === GameState.PLAYING && (
        <div className={`px-4 py-2 text-xs flex items-center justify-between border-b ${
          autoSendTimeRemaining <= 10
            ? isDarkMode 
              ? 'bg-red-900/30 text-red-300 border-red-800 animate-pulse' 
              : 'bg-red-50 text-red-600 border-red-200 animate-pulse'
            : isDarkMode 
              ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800' 
              : 'bg-yellow-50 text-yellow-600 border-yellow-200'
        }`}>
          <div className="flex items-center gap-2">
            <Timer size={14} />
            <span>Auto-send in {autoSendTimeRemaining}s</span>
          </div>
          <div className="text-xs opacity-75">
            Message will send automatically if you don't act
          </div>
        </div>
      )}
      
      {/* Voting UI */}
      {votingState && gameState === GameState.CHOOSING_STORY && !votingResult && (
        <div className={`flex-1 flex flex-col p-4 overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Vote size={24} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                Vote for Next Adventure
              </h2>
            </div>
            <div className={`flex items-center justify-center gap-2 text-lg font-mono ${
              votingTimeRemaining <= 5 
                ? 'text-red-500 animate-pulse' 
                : isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <Clock size={18} />
              <span>{votingTimeRemaining}s remaining</span>
            </div>
          </div>
          
          <div className="flex flex-col gap-3 flex-1 min-h-0">
            {votingState.options.map((option, idx) => {
              const voteCount = getVoteCount(option.id);
              const voters = getVoters(option.id);
              const isMyChoice = myVote === option.id;
              
              return (
                <button
                  key={option.id}
                  onClick={() => handleVote(option.id)}
                  disabled={!!myVote}
                  className={`flex-1 p-4 rounded-xl border-2 text-left transition-all ${
                    isMyChoice
                      ? isDarkMode 
                        ? 'bg-green-900/50 border-green-500' 
                        : 'bg-green-50 border-green-500'
                      : myVote
                        ? isDarkMode 
                          ? 'bg-gray-800 border-gray-700 opacity-60' 
                          : 'bg-gray-50 border-gray-200 opacity-60'
                        : isDarkMode 
                          ? 'bg-gray-800 border-gray-700 hover:border-blue-500 hover:bg-gray-750' 
                          : 'bg-gray-50 border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Option {String.fromCharCode(65 + idx)}
                    </span>
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                      isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}>
                      <Users size={12} />
                      <span>{voteCount}</span>
                    </div>
                  </div>
                  <p className={`text-sm leading-relaxed mt-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {option.preview}
                  </p>
                  {voters.length > 0 && (
                    <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Voted: {voters.join(', ')}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {myVote && (
            <div className={`mt-4 text-center text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
              ✓ You voted! Waiting for others...
            </div>
          )}
        </div>
      )}
      
      {/* Voting Result Display */}
      {votingResult && (
        <div className={`flex-1 flex flex-col items-center justify-center p-8 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <div className={`text-6xl mb-4`}>🎉</div>
          <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            {votingResult.wasTie ? 'Tie-Breaker!' : 'Adventure Selected!'}
          </h2>
          {votingResult.wasTie && (
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              There was a tie, so we picked randomly!
            </p>
          )}
          <p className={`text-center leading-relaxed max-w-md ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {votingResult.selectedOption.preview}
          </p>
          <div className={`mt-4 animate-pulse ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Starting adventure...
          </div>
        </div>
      )}
      
      {/* Messages (only show when not voting) */}
      {!votingState && !votingResult && (
        <MessageList
          messages={messages}
          isTyping={isProcessingAI || gameState === GameState.LOADING_INITIAL}
          isThinking={isThinking}
          streamingText={streamingText}
          isDarkMode={isDarkMode}
        />
      )}
      
      {/* Countdown */}
      {countdown && gameState === GameState.PLAYING && !votingState && (
        <CountdownDisplay
          remaining={countdown.remaining}
          label={countdown.label}
          isDarkMode={isDarkMode}
        />
      )}
      
      {/* Keyboard with Emoji Button */}
      {gameState === GameState.PLAYING && !votingState && !votingResult && (
        <div className={`relative border-t ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-200 border-gray-300'}`}>
          {/* Emoji Reaction Button - positioned absolute in top-right */}
          <div className="absolute top-3 right-3 z-10">
            <EmojiReactionButton
              onEmojiSelect={handleEmojiSelect}
              isDarkMode={isDarkMode}
            />
          </div>
          
          <PredictiveKeyboard
            currentInput={currentInput}
            suggestions={suggestions}
            isLoadingSuggestions={isLoadingSuggestions && !isProcessingAI}
            onSelectSuggestion={handleSelectSuggestion}
            onSend={handleSendMessage}
            onBackspace={handleBackspace}
            disabled={isProcessingAI || (!isMyTurn && session.turnMode === 'per_message')}
            isDarkMode={isDarkMode}
          />
        </div>
      )}
      
      {/* Emoji Animations */}
      <EmojiAnimation
        reactions={emojiReactions}
        onComplete={handleEmojiComplete}
      />
      
      {/* Loading state */}
      {gameState === GameState.LOADING_INITIAL && (
        <div className={`flex items-center justify-center p-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin border-blue-500" />
            <span>Starting adventure...</span>
          </div>
        </div>
      )}
    </div>
  );
};
